// eslint-disable-next-line no-restricted-imports
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import type { FC } from "react";

import { SectionSettingsGeneralForm } from "components/forms/SectionSettingsGeneralForm";
import { SectionSettingsGradeForm } from "components/forms/SectionSettingsGradeForm";
import { SectionSettingsSectionInfoForm } from "components/forms/SectionSettingsSectionInfoForm";
import { SectionSettingsVirtualClassForm } from "components/forms/SectionSettingsVirtualClassForm";
import { SectionKind } from "components/server-types";
import { t } from "i18n/i18n";
import type { ResourceKey } from "i18n/i18next";
import { Dialog } from "mds/components/Dialog";
import { IconText } from "mds/components/IconText";
import { AssessPointsMarkerIcon, BookSectionIcon, GearSettingsIcon, VideoIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  courseSelectors,
  selectDesignatedSection,
  selectSectionSettingsTabIndex,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { getCourseAndSectionDisplayTitle } from "utils/course";
import { isAVEnabled } from "utils/waffle-flags";

// Used to open the Section grade tab from the SectionScoreCard.
export const SECTION_SETTINGS_GRADE_TAB_INDEX = 2;

export const SectionSettingsModal: FC = () => {
  const dispatch = useAppDispatch();
  const tabIndex = useAppSelector(selectSectionSettingsTabIndex);
  const designatedSection = useAppSelector(selectDesignatedSection);
  const course = useAppSelector((s) =>
    designatedSection ? courseSelectors.selectById(s, designatedSection.course_id) : null,
  );

  const closeSectionSettingsModal = () => {
    dispatch(viewStateActions.setSectionSettingsTabIndex(undefined));
  };

  const sectionKind = designatedSection?.kind ?? SectionKind.UNLINKED;
  const modalTitleKey = `section.titles.${sectionKind}` as ResourceKey;
  const sectionSubtitle =
    designatedSection && course
      ? getCourseAndSectionDisplayTitle(course, designatedSection)
      : (designatedSection?.title ?? "");

  const tabClassName =
    "group/tab -mb-0.5 border-b-2 border-blue-tint-90 py-3 text-black-tint-20 hover:border-blue-tint-70 data-[selected]:border-blue";

  const dialogPanelClassName = isAVEnabled() ? "w-[550px]" : "w-[450px]";

  return (
    <Dialog
      // Section settings modal needs to appear above screenshare
      className="z-[52]"
      description={
        sectionSubtitle ? (
          <div className="mt-1 text-sm font-normal leading-normal text-black-tint-40">
            {sectionSubtitle}
          </div>
        ) : undefined
      }
      dialogPanelClassName={dialogPanelClassName}
      open={tabIndex !== undefined}
      title={t(modalTitleKey)}
      onClose={closeSectionSettingsModal}
    >
      {/*
      I (David) tried to use the `<TabBar>` component from MDS for this tab bar,
      but found a strange bug: if you switch to the Course Score tab, fill out
      the form with invalid values (e.g. percentages that do not add up to 100),
      and attempt to submit, then the form does *not* display the error message, as it
      is supposed to. Instead, it resets the values in the form to the existing values
      from the course, with no visual feedback at all. I suspect this is due to some kind
      of re-rendering issue with the `<TabBar>` component, but I couldn't figure out
      how to fix it. So I am using the tab components from Headless UI instead,
      which do not have this issue.
      (`<TabBar>` is a wrapper around the Headless UI tab components, anyway.)
      */}
      <TabGroup
        className="mt-3"
        selectedIndex={tabIndex}
        onChange={(index) => dispatch(viewStateActions.setSectionSettingsTabIndex(index))}
      >
        <TabList className="flex flex-row gap-8 border-b-2 border-blue-tint-90 text-sm font-medium">
          <Tab className={tabClassName}>
            <IconText
              iconStart={<GearSettingsIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("settings.general")}
            />
          </Tab>

          <Tab className={tabClassName}>
            <IconText
              iconStart={<BookSectionIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.section_info")}
            />
          </Tab>

          <Tab className={tabClassName}>
            <IconText
              iconStart={<AssessPointsMarkerIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("settings.section_score")}
            />
          </Tab>

          {isAVEnabled() && (
            <Tab className={tabClassName}>
              <IconText
                iconStart={<VideoIcon className="group-data-[selected]/tab:text-blue" />}
                text={t("glossary.virtual_class")}
              />
            </Tab>
          )}
        </TabList>

        <TabPanels>
          <TabPanel>
            <SectionSettingsGeneralForm className="pt-5" onClose={closeSectionSettingsModal} />
          </TabPanel>

          <TabPanel>
            <SectionSettingsSectionInfoForm className="pt-5" onClose={closeSectionSettingsModal} />
          </TabPanel>

          <TabPanel>
            <SectionSettingsGradeForm className="pt-5" onClose={closeSectionSettingsModal} />
          </TabPanel>

          {isAVEnabled() && (
            <TabPanel>
              <SectionSettingsVirtualClassForm
                className="pt-5"
                onClose={closeSectionSettingsModal}
              />
            </TabPanel>
          )}
        </TabPanels>
      </TabGroup>
    </Dialog>
  );
};
